import { Application, AllAppsNonProductionEnvironmentsEntity } from 'src/components/context/AppContextModels';
import { ENVIRONMENT_VARIABLES } from 'src/constants/EnvironmentConstants';
import { EmbeddableProductType } from '../types/EmbeddingInterface';
import { FINSUITE_DOMAINS } from 'src/constants/AppConstants';
import { EMBED_TO_PARENT_MESSAGES } from 'src/constants/EmbedMessageConstants';
import { logger } from 'src/logger';

/**
 * SECURITY: Only trusted, cross-origin `https:` URLs are allowed to be loaded into
 * the embedded-app iframe.
 *
 * Rejecting non-`https:` schemes at the client boundary blocks stored-XSS payloads
 * such as `javascript:parent.document.title='XSS_'+parent.localStorage.length;//-`
 * from ever reaching the iframe `src` attribute. In addition, a host allowlist
 * ensures the framed document is a trusted Amazon-owned origin and — critically —
 * NOT FinSuite's own origin: because the iframe sandbox pairs `allow-scripts` with
 * `allow-same-origin` (see IFrameComponent.tsx), same-origin content could reach
 * `parent.localStorage`, so a self-embed must never be handed to the iframe. A
 * genuinely cross-origin https app gets its OWN origin and cannot reach the parent.
 *
 * This mirrors the authoritative server-side allowlist in the backend
 * (finsuite_resolver_handlers.py). Keep the two lists in sync.
 *
 * Returns the normalized URL string on success, or `undefined` on rejection.
 * Rejections are logged (warn) so admins can spot mis-configured applications.
 */
// Trusted Amazon-owned host allowlist. MUST stay in sync with ALLOWED_HOST_SUFFIXES /
// ALLOWED_HOSTS_EXACT in the backend finsuite_resolver_handlers.py.
const ALLOWED_HOST_SUFFIXES = ['.amazon.com', '.amazon.dev', '.a2z.com', '.aws.dev', '.online.tableau.com'] as const;
const ALLOWED_HOSTS_EXACT = new Set(['amazon.com', 'amazon.dev', 'a2z.com', 'aws.dev']);

// Explicit http localhost origins allowed ONLY in non-prod stages, for embed testing
// of the other apps we manage (xPT :4444, Verity :4555). Port-exact and http-only.
// FinSuite's own dev port (4111) is intentionally excluded — embedding it would be a
// same-origin frame under allow-scripts + allow-same-origin. MUST stay in sync with
// ALLOWED_LOCALHOST_ORIGINS in the backend url_validation.py.
export const ALLOWED_LOCALHOST_ORIGINS = new Set(['http://localhost:4444', 'http://localhost:4555']);

export const sanitizeEmbeddingUrl = (rawUrl: string | undefined | null): string | undefined => {
  if (!rawUrl || typeof rawUrl !== 'string') return undefined;
  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    logger.warn(`Embedding URL rejected — not a valid absolute URL: ${rawUrl}`);
    return undefined;
  }
  // Non-prod escape hatch: explicit http localhost origins for embed testing.
  if (ENVIRONMENT_VARIABLES.env.Stage !== 'prod' && ALLOWED_LOCALHOST_ORIGINS.has(parsed.origin)) {
    return parsed.toString();
  }
  if (parsed.protocol !== 'https:') {
    logger.warn(`Embedding URL rejected — non-https scheme "${parsed.protocol}" for URL: ${rawUrl}`);
    return undefined;
  }
  const host = parsed.hostname.toLowerCase();
  // Reject FinSuite's OWN origin first. Under `allow-scripts` + `allow-same-origin`,
  // same-origin content can reach `parent.localStorage`; a self-embed must never be
  // handed to the iframe. Host-based match avoids the substring pitfall of `includes`.
  if (FINSUITE_DOMAINS.some((domain) => host === domain || host.endsWith(`.${domain}`))) {
    logger.warn(`Embedding URL rejected — self-embed of FinSuite origin: ${rawUrl}`);
    return undefined;
  }
  if (!ALLOWED_HOSTS_EXACT.has(host) && !ALLOWED_HOST_SUFFIXES.some((suffix) => host.endsWith(suffix))) {
    logger.warn(`Embedding URL rejected — host not in allowlist: ${host}`);
    return undefined;
  }
  return parsed.toString();
};

// Map for easily extendible product type mapping
const EMBEDDABLE_PRODUCT_MAP = new Map([
  ['Cloudscape (Polaris)', EmbeddableProductType.CLOUDSCAPE_POLARIS],
  ['Legacy Polaris', EmbeddableProductType.LEGACY_POLARIS],
  ['Prophecy', EmbeddableProductType.PROPHECY],
  ['Skynet (Tableau)', EmbeddableProductType.SKYNET_TABLEAU],
  ['Quicksight', EmbeddableProductType.AMAZON_QUICK]
]);

/**
 * Extracts the product type from the application's toolUI label
 * and resolves it to a normalized EmbeddableProductType via the product map.
 */
export const getProductType = (application?: Application): string => {
  if (!application) return EmbeddableProductType.UNKNOWN;
  const toolLabel = application.toolUI?.[0]?.label || EmbeddableProductType.UNKNOWN;
  return EMBEDDABLE_PRODUCT_MAP.get(toolLabel) || EmbeddableProductType.UNKNOWN;
};

/**
 * Determines if an application is eligible for embedding functionality.
 *
 * An application is eligible if it has a recognized product type that
 * supports embedding capabilities.
 *
 * @param application - The application to evaluate for embedding eligibility
 * @returns True if the application supports embedding, false otherwise
 */
export const isEligibleToEnableEmbedding = (application: Application): boolean => {
  const url = getEmbeddingURL(application);

  // to avoid self embedding
  const found = FINSUITE_DOMAINS.find((domain) => url?.includes(domain));
  if (found) return false;

  const productType = getProductType(application);
  return productType !== EmbeddableProductType.UNKNOWN;
};

/**
 * Determines if embedding is both supported and enabled for an application.
 *
 * @param application - The application to check
 * @returns True if embedding is eligible and explicitly enabled
 */
export const isEmbeddingEnabled = (application: Application): boolean => {
  if (!application) return false;
  return isEligibleToEnableEmbedding(application) && application.embeddingEnabled === true;
};

/**
 * Returns the list of non-production environments available for embedding.
 * Only applicable in non-prod stages.
 */
export const getNonProdEnvironments = (application: Application): AllAppsNonProductionEnvironmentsEntity[] => {
  return application.nonProductionEnvironments?.filter((env) => env.link) || [];
};

/**
 * Discriminated result of resolving the embedding URL for an application.
 *
 * `missing`  — no candidate URL was configured for the current stage. The
 *              admin form has not been filled in, or the non-prod envs array
 *              is empty. UX: "Missing Embed URL — please configure a link".
 * `invalid`  — a candidate URL exists but was rejected by `sanitizeEmbeddingUrl`
 *              (non-https, `javascript:`, `data:`, malformed, …). This is the
 *              stored-XSS defense: the row exists but the client refuses to
 *              hand it to an iframe. UX: "Invalid URL — cannot be displayed".
 * `ok`       — a candidate URL exists and passed the https-only sanitizer.
 *              UX: proceed to render the iframe.
 */
export type EmbeddingUrlResolution = { status: 'ok'; url: string } | { status: 'missing' } | { status: 'invalid'; raw: string };

/**
 * Selects the raw candidate URL for the current stage without applying any
 * sanitization. Split out so `resolveEmbeddingUrl` can distinguish "no
 * candidate at all" (missing) from "candidate present but rejected" (invalid).
 */
const selectCandidateUrl = (application: Application, selectedEnvLink?: string): string | undefined => {
  const isNonProd = ENVIRONMENT_VARIABLES.env.Stage !== 'prod';
  if (isNonProd) {
    if (selectedEnvLink) return selectedEnvLink;
    const envs = getNonProdEnvironments(application);
    return envs[0]?.link || undefined;
  }
  return application.applicationLink || undefined;
};

/**
 * Resolves the embedding URL and reports *why* it failed when it fails.
 *
 * Callers that only care about the happy-path URL should keep using
 * `getEmbeddingURL` (a thin wrapper). Callers that need to render different
 * UX for "missing" vs "invalid" (e.g. `EmbedApp.tsx` → `EmbedAppErrorState`)
 * should use this function.
 */
export const resolveEmbeddingUrl = (application: Application, selectedEnvLink?: string): EmbeddingUrlResolution => {
  const candidate = selectCandidateUrl(application, selectedEnvLink);
  if (!candidate || candidate.trim() === '') return { status: 'missing' };

  // SECURITY: enforce https-only at the render boundary. Any non-https scheme
  // (e.g. `javascript:`, `data:`, `http:`) is rejected before it can reach an
  // iframe `src` or a window.location assignment. See sanitizeEmbeddingUrl.
  const sanitized = sanitizeEmbeddingUrl(candidate);
  return sanitized ? { status: 'ok', url: sanitized } : { status: 'invalid', raw: candidate };
};

/**
 * Retrieves the appropriate embedding URL based on the current environment.
 *
 * In non-production environments, uses the provided selectedEnvLink (user-chosen)
 * or falls back to the first available non-production environment.
 * In production, returns the main application URL.
 *
 * Returns `undefined` for both the "missing" and "invalid" cases; callers that
 * need to distinguish those should use `resolveEmbeddingUrl` instead.
 *
 * @param application - The application containing URL configurations
 * @param selectedEnvLink - Optional user-selected non-prod environment link
 * @returns The embedding URL or undefined if not available
 */
export const getEmbeddingURL = (application: Application, selectedEnvLink?: string): string | undefined => {
  const resolution = resolveEmbeddingUrl(application, selectedEnvLink);
  return resolution.status === 'ok' ? resolution.url : undefined;
};

export const EmbeddingURLErrorMessages = {
  URL_NOT_CONFIGURED: `URL is not configured`,
  URL_INVALID: `URL is invalid`,
  URL_CONFIGURE_UNKNOWN: `Unknown error while configuring URL`
};

// ─── Cross-Frame Messaging Utilities ───────────────────────────────────────────
// These utilities support the postMessage protocol between FinSuite (parent)
// and embedded applications (child iframe). See EmbedMessageConstants.ts for
// the full protocol documentation.

/**
 * Sends a postMessage to the currently embedded iframe.
 *
 * @param type - The message type constant (from PARENT_TO_EMBED_MESSAGES)
 * @param payload - Optional payload data to send with the message
 * @returns true if the message was sent, false if no iframe was found
 */
export const postMessageToEmbeddedIframe = (type: string, payload?: Record<string, unknown>): boolean => {
  const iframe = document.querySelector('iframe[data-embed-app]') as HTMLIFrameElement;
  if (!iframe?.contentWindow) return false;
  iframe.contentWindow.postMessage({ type, payload }, '*');
  return true;
};

/**
 * Checks if a MessageEvent is the embed ready handshake signal from an embedded app.
 *
 * @param event - The MessageEvent to check
 * @returns true if the event is a FINSUITE_EMBED_READY message
 */
export const isEmbedReadyMessage = (event: MessageEvent): boolean => {
  return event.data?.type === EMBED_TO_PARENT_MESSAGES.EMBED_READY;
};

/**
 * Sanitizes a path reported by an embedded app via FINSUITE_CURRENT_PATH.
 *
 * SECURITY: the reported value is untrusted. Only a RELATIVE, same-document path is
 * allowed — it must begin with exactly one '/'. This rejects protocol-relative
 * ('//evil.com'), absolute ('https://…'), scheme ('javascript:…') and backslash
 * ('/\\evil.com') forms that could redirect the external link off the app's origin.
 *
 * Returns the trimmed path, or undefined when it is not a safe relative path.
 */
export const sanitizeReportedPath = (rawPath: unknown): string | undefined => {
  if (typeof rawPath !== 'string') return undefined;
  const path = rawPath.trim();
  // Must start with a single '/', not '//' (protocol-relative) or '/\' (backslash trick).
  if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return undefined;
  return path;
};

/**
 * Composes the external ("open in new tab") URL from a trusted base URL and an untrusted
 * reported path.
 *
 * SECURITY: resolves the path against the base via the URL constructor and asserts the
 * result stays on the SAME ORIGIN as the base. Any path that resolves to a different origin
 * (absolute URL, protocol-relative, '..' escape) is rejected and the base URL is returned
 * unchanged, so the external link can only ever deep-link WITHIN the embedded app. An
 * empty / '/' / invalid path also yields the base.
 */
export const composeExternalUrlWithPath = (baseUrl: string, reportedPath: string | null | undefined): string => {
  const safePath = sanitizeReportedPath(reportedPath);
  if (!safePath) return baseUrl;
  try {
    const base = new URL(baseUrl);
    const resolved = new URL(safePath, base);
    if (resolved.origin !== base.origin) {
      logger.warn(`Reported path rejected — resolves off-origin (${resolved.origin} !== ${base.origin}): ${reportedPath}`);
      return baseUrl;
    }
    return resolved.toString();
  } catch {
    logger.warn(`Reported path rejected — could not compose against base: ${reportedPath}`);
    return baseUrl;
  }
};
